<template>
  <!-- Modal Mode -->
  <div v-if="show && !isOwnProfile" class="modal-overlay">
    <div class="modal-container">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditing ? "Editar Usuario" : "Crear Usuario" }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="mb-3">
              <label for="name" class="form-label">Nombre</label>
              <input
                type="text"
                class="form-control"
                id="name"
                v-model="formData.name"
                required
              />
            </div>
            <div class="mb-3">
              <label for="jobTitle" class="form-label">{{ $t("Job title") }}</label>
              <input
                type="text"
                class="form-control"
                id="jobTitle"
                v-model="formData.jobTitle"
                autocomplete="off"
              />
              <small class="text-muted">{{
                $t("Shown under the name in the document signature.")
              }}</small>
            </div>
            <div class="mb-3">
              <label for="email" class="form-label">Email</label>
              <input
                type="email"
                class="form-control"
                id="email"
                v-model="formData.email"
                :readonly="isOwnProfile"
                :disabled="isOwnProfile"
                :style="isOwnProfile ? 'background-color: #f8f9fa;' : ''"
                required
              />
              <small v-if="isOwnProfile" class="text-muted"
                >El email no puede ser modificado</small
              >
            </div>
            <div v-if="isEditing" class="mb-3">
              <label for="notificationEmail" class="form-label">{{ $t("Notification email") }}</label>
              <input
                type="email"
                class="form-control"
                id="notificationEmail"
                v-model="formData.notificationEmail"
                autocomplete="off"
                :placeholder="formData.email"
              />
              <small class="text-muted">{{
                $t("Notices by email arrive here. Empty = your login email.")
              }}</small>
            </div>
            <div class="mb-3">
              <label for="password" class="form-label">Contraseña</label>
              <div v-if="isEditing" class="mb-2">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="changePassword"
                    v-model="changePassword"
                  />
                  <label class="form-check-label" for="changePassword">
                    Cambiar contraseña
                  </label>
                </div>
              </div>
              <input
                type="password"
                class="form-control"
                id="password"
                v-model="formData.password"
                :required="!isEditing || changePassword"
                :disabled="isEditing && !changePassword"
              />
            </div>
            <div v-if="!hideRole" class="mb-3">
              <label for="role" class="form-label">Rol</label>
              <select
                class="form-select"
                id="role"
                v-model="formData.role"
                required
              >
                <option
                  v-for="role in availableRoles"
                  :key="role._id"
                  :value="role.name"
                >
                  {{ role.description }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label for="language" class="form-label">{{ $t("Notices and support language") }}</label>
              <select
                class="form-select"
                id="language"
                v-model="formData.language"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="it">Italiano</option>
                <option value="pt">Português</option>
                <option value="ru">Русский</option>
                <option value="zh">中文</option>
                <option value="ja">日本語</option>
                <option value="ko">한국어</option>
                <option value="ar">العربية</option>
              </select>
              <small class="form-text text-muted">
                {{ $t("Alerts, notifications, validations and technical support.") }}
              </small>
            </div>
            <div class="mb-3">
              <label for="business_language" class="form-label">{{ $t("App language") }}</label>
              <select
                class="form-select"
                id="business_language"
                v-model="formData.business_language"
              >
                <option value="">{{ $t("Same as notices and support") }}</option>
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="pt">Português</option>
                <option value="uk">Українська</option>
              </select>
              <small class="form-text text-muted">
                {{ $t("Menu, buttons, fields and columns: everything except notices and support.") }}
              </small>
            </div>
            <div class="mb-3">
              <label class="form-label">Avatar</label>
              <div class="d-flex align-items-center gap-3">
                <div class="flex-shrink-0 w-25">
                  <img
                    v-if="avatarPreview || formData.avatar"
                    :src="avatarPreview || formData.avatar"
                    class="rounded-circle"
                    width="100"
                    height="100"
                    alt="User avatar"
                    style="object-fit: cover"
                  />
                  <div
                    v-else
                    class="rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style="width: 100px; height: 100px"
                  >
                    <span class="text-muted fs-4">{{
                      formData.name.charAt(0)
                    }}</span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <input
                    type="file"
                    class="form-control"
                    accept="image/*"
                    @change="handleAvatarChange"
                    ref="avatarInput"
                  />
                  <small class="text-muted"
                    >Formatos permitidos: JPG, PNG, GIF. Tamaño máximo:
                    2MB</small
                  >
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Signature</label>
              <div class="d-flex align-items-center gap-3">
                <div class="flex-shrink-0 w-25">
                  <img
                    v-if="signaturePreview || formData.signature"
                    :src="signaturePreview || formData.signature"
                    class="rounded"
                    width="120"
                    height="60"
                    alt="User signature"
                    style="object-fit: contain; border: 1px solid #ddd"
                  />
                  <div
                    v-else
                    class="rounded bg-light d-flex align-items-center justify-content-center"
                    style="width: 120px; height: 60px; border: 1px solid #ddd"
                  >
                    <span class="text-muted fs-6">No signature</span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <input
                    type="file"
                    class="form-control"
                    accept="image/*"
                    @change="handleSignatureChange"
                    ref="signatureInput"
                  />
                  <small class="text-muted"
                    >Formatos permitidos: JPG, PNG, GIF. Tamaño máximo:
                    2MB</small
                  >
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeModal"
              >
                Cancelar
              </button>
              <button type="submit" class="btn btn-primary" :disabled="isUploading">
                <span v-if="isUploading">
                  <i class="fas fa-spinner fa-spin me-1"></i> Subiendo...
                </span>
                <span v-else>{{ isEditing ? "Guardar" : "Crear" }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Page Mode (for own profile) -->
  <div v-else-if="isOwnProfile">
    <div class="card-body">
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="name" class="form-label">Nombre</label>
              <input
                type="text"
                class="form-control"
                id="name"
                v-model="formData.name"
                required
              />
            </div>
            <div class="mb-3">
              <label for="jobTitleInline" class="form-label">{{ $t("Job title") }}</label>
              <input
                type="text"
                class="form-control"
                id="jobTitleInline"
                v-model="formData.jobTitle"
                autocomplete="off"
              />
              <small class="text-muted">{{
                $t("Shown under the name in the document signature.")
              }}</small>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label for="email" class="form-label">Email</label>
              <input
                type="email"
                class="form-control"
                id="email"
                v-model="formData.email"
                readonly
                disabled
                style="background-color: #f8f9fa"
              />
              <small class="text-muted">El email no puede ser modificado</small>
            </div>
            <div class="mb-3">
              <label for="notificationEmailInline" class="form-label">{{ $t("Notification email") }}</label>
              <input
                type="email"
                class="form-control"
                id="notificationEmailInline"
                v-model="formData.notificationEmail"
                autocomplete="off"
                :placeholder="formData.email"
              />
              <small class="text-muted">{{
                $t("Notices by email arrive here. Empty = your login email.")
              }}</small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="password" class="form-label">Contraseña</label>
              <div class="mb-2">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="changePassword"
                    v-model="changePassword"
                  />
                  <label class="form-check-label" for="changePassword">
                    Cambiar contraseña
                  </label>
                </div>
              </div>
              <input
                type="password"
                class="form-control"
                id="password"
                v-model="formData.password"
                :required="changePassword"
                :disabled="!changePassword"
              />
            </div>
            <TwoFactorProfileSetup />
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label for="language" class="form-label">{{ $t("Notices and support language") }}</label>
              <select
                class="form-select"
                id="language"
                v-model="formData.language"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="it">Italiano</option>
                <option value="pt">Português</option>
                <option value="ru">Русский</option>
                <option value="zh">中文</option>
                <option value="ja">日本語</option>
                <option value="ko">한국어</option>
                <option value="ar">العربية</option>
              </select>
              <small class="form-text text-muted">
                {{ $t("Alerts, notifications, validations and technical support.") }}
              </small>
            </div>
            <div class="mb-3">
              <label for="business_language" class="form-label">{{ $t("App language") }}</label>
              <select
                class="form-select"
                id="business_language"
                v-model="formData.business_language"
              >
                <option value="">{{ $t("Same as notices and support") }}</option>
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="pt">Português</option>
                <option value="uk">Українська</option>
              </select>
              <small class="form-text text-muted">
                {{ $t("Menu, buttons, fields and columns: everything except notices and support.") }}
              </small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label">Avatar</label>
              <div class="d-flex align-items-center gap-3">
                <div class="flex-shrink-0 w-25 idioma">
                  <img
                    v-if="avatarPreview || formData.avatar"
                    :src="avatarPreview || formData.avatar"
                    class="rounded-circle"
                    width="80"
                    height="80"
                    alt="User avatar"
                    style="object-fit: cover"
                  />
                  <div
                    v-else
                    class="rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style="width: 80px; height: 80px"
                  >
                    <span class="text-muted fs-5">{{
                      formData.name.charAt(0)
                    }}</span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <input
                    type="file"
                    class="form-control"
                    accept="image/*"
                    @change="handleAvatarChange"
                    ref="avatarInput"
                  />
                  <small class="text-muted"
                    >Formatos permitidos: JPG, PNG, GIF. Tamaño máximo:
                    2MB</small
                  >
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label">Signature</label>
              <div class="d-flex align-items-center gap-3">
                <div class="flex-shrink-0 w-25 idioma">
                  <img
                    v-if="signaturePreview || formData.signature"
                    :src="signaturePreview || formData.signature"
                    class="rounded"
                    width="120"
                    height="60"
                    alt="User signature"
                    style="object-fit: contain; border: 1px solid #ddd"
                  />
                  <div
                    v-else
                    class="rounded bg-light d-flex align-items-center justify-content-center"
                    style="width: 120px; height: 60px; border: 1px solid #ddd"
                  >
                    <span class="text-muted fs-6">No signature</span>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <input
                    type="file"
                    class="form-control"
                    accept="image/*"
                    @change="handleSignatureChange"
                    ref="signatureInput"
                  />
                  <small class="text-muted"
                    >Formatos permitidos: JPG, PNG, GIF. Tamaño máximo:
                    2MB</small
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button type="submit" class="btn btn-primary" :disabled="isUploading">
            <span v-if="isUploading">
              <i class="fas fa-spinner fa-spin me-2"></i>Subiendo...
            </span>
            <span v-else>
              <i class="fas fa-save me-2"></i>Guardar Cambios
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { wencesApi } from "@/api/wencesApi";
import { syncI18nLocaleFromUser } from "@/i18n";
import Swal from "sweetalert2";
import TwoFactorProfileSetup from "@/components/users/TwoFactorProfileSetup.vue";

const props = defineProps({
  user: {
    type: Object,
    required: false,
    default: null,
  },
  show: {
    type: Boolean,
    default: false,
  },
  roles: {
    type: Array,
    required: false,
    default: () => [],
  },
  isOwnProfile: {
    type: Boolean,
    default: false,
  },
  hideRole: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:show", "user-updated"]);

const isEditing = computed(() => !!props.user);

// Estado para verificar si el usuario tiene permisos de admin
const isAdmin = ref(false);

// Obtener los permisos del usuario actual desde el backend
const fetchUserPermissions = async () => {
  try {
    const response = await wencesApi.get("/api/users/myprofile");
    if (response.data.ok) {
      const userData = response.data.data;
      let roleId;

      // Obtener el ID del rol
      if (typeof userData.role === "string") {
        roleId = userData.role;
      } else if (userData.role && userData.role._id) {
        roleId = userData.role._id;
      }

      if (roleId) {
        // Obtener los permisos del rol
        const roleResponse = await wencesApi.get(`/api/roles/${roleId}`);
        if (
          roleResponse.data.success &&
          roleResponse.data.data &&
          roleResponse.data.data.permissions
        ) {
          const permissions = roleResponse.data.data.permissions;
          // Verificar si tiene el permiso 'all_access'
          isAdmin.value = permissions.includes("all_access");
        }
      }
    }
  } catch (error) {
    console.error("Error fetching user permissions:", error);
    isAdmin.value = false;
  }
};

// Filtrar roles disponibles - solo permitir roles con 'all_access' si el usuario actual tiene 'all_access'
const availableRoles = computed(() => {
  if (isAdmin.value) {
    return props.roles; // Mostrar todos los roles si tiene all_access
  } else {
    // Excluir roles que tengan 'all_access' si el usuario no lo tiene
    return props.roles.filter((role) => {
      // Si el rol tiene permisos definidos, verificar si tiene 'all_access'
      if (role.permissions && Array.isArray(role.permissions)) {
        return !role.permissions.includes("all_access");
      }
      // Si no tiene permisos definidos, permitir (roles básicos)
      return true;
    });
  }
});
const changePassword = ref(false);

const formData = ref({
  _id: props.user?._id || "",
  name: props.user?.name || "",
  email: props.user?.email || "",
  password: "",
  role: props.user?.role || "user",
  avatar: props.user?.avatar || "",
  signature: props.user?.signature || "",
  jobTitle: props.user?.jobTitle || "",
  language: props.user?.language || "es",
  business_language: props.user?.business_language || "",
  telegramUsername: props.user?.telegramUsername || "",
  notificationEmail: props.user?.notificationEmail || "",
});

const avatarPreview = ref(null);
const avatarInput = ref(null);
const signaturePreview = ref(null);
const signatureInput = ref(null);
const pendingAvatarFile = ref(null);
const pendingSignatureFile = ref(null);
const isUploading = ref(false);

// Función para subir imagen a S3
const uploadImageToS3 = async (file) => {
  const formDataUpload = new FormData();
  formDataUpload.append('image', file);

  try {
    const response = await wencesApi.post('/api/upload/upload-public', formDataUpload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return { ok: true, url: response.data.url };
  } catch (error) {
    console.error('Error uploading image:', error);
    return { ok: false, message: error.response?.data?.message || 'Error al subir la imagen' };
  }
};

const handleAvatarChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validar tamaño (2MB máximo)
  if (file.size > 2 * 1024 * 1024) {
    Swal.fire({
      title: "Error",
      text: "El archivo es demasiado grande. El tamaño máximo permitido es 2MB.",
      icon: "error",
    });
    event.target.value = "";
    return;
  }

  // Validar tipo de archivo
  if (!file.type.startsWith("image/")) {
    Swal.fire({
      title: "Error",
      text: "Por favor, selecciona un archivo de imagen válido.",
      icon: "error",
    });
    event.target.value = "";
    return;
  }

  // Guardar el archivo para subirlo al hacer submit
  pendingAvatarFile.value = file;

  // Crear preview local
  const reader = new FileReader();
  reader.onload = (e) => {
    avatarPreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const handleSignatureChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validar tamaño (2MB máximo)
  if (file.size > 2 * 1024 * 1024) {
    Swal.fire({
      title: "Error",
      text: "El archivo es demasiado grande. El tamaño máximo permitido es 2MB.",
      icon: "error",
    });
    event.target.value = "";
    return;
  }

  // Validar tipo de archivo
  if (!file.type.startsWith("image/")) {
    Swal.fire({
      title: "Error",
      text: "Por favor, selecciona un archivo de imagen válido.",
      icon: "error",
    });
    event.target.value = "";
    return;
  }

  // Guardar el archivo para subirlo al hacer submit
  pendingSignatureFile.value = file;

  // Crear preview local
  const reader = new FileReader();
  reader.onload = (e) => {
    signaturePreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

watch(
  () => props.user,
  (newUser) => {
    if (newUser) {
      formData.value = {
        _id: newUser._id,
        name: newUser.name,
        email: newUser.email,
        password: "",
        role: newUser.role,
        avatar: newUser.avatar,
        signature: newUser.signature,
        jobTitle: newUser.jobTitle || "",
        language: newUser.language || "es",
        business_language: newUser.business_language || "",
        telegramUsername: newUser.telegramUsername || "",
        notificationEmail: newUser.notificationEmail || "",
      };
      avatarPreview.value = null;
      pendingAvatarFile.value = null;
      pendingSignatureFile.value = null;
      changePassword.value = false;
    } else {
      formData.value = {
        _id: "",
        name: "",
        email: "",
        password: "",
        role: "user",
        avatar: "",
        signature: "",
        jobTitle: "",
        language: "es",
        business_language: "",
        telegramUsername: "",
        notificationEmail: "",
      };
      avatarPreview.value = null;
      signaturePreview.value = null;
      pendingAvatarFile.value = null;
      pendingSignatureFile.value = null;
      changePassword.value = false;
    }
  },
  { immediate: true, deep: true }
);

const closeModal = () => {
  emit("update:show", false);
  changePassword.value = false;
  avatarPreview.value = null;
  signaturePreview.value = null;
  pendingAvatarFile.value = null;
  pendingSignatureFile.value = null;
  if (avatarInput.value) {
    avatarInput.value.value = "";
  }
  if (signatureInput.value) {
    signatureInput.value.value = "";
  }
};

const handleSubmit = async () => {
  try {
    // Validar que solo un usuario con 'all_access' puede asignar roles con 'all_access'
    const selectedRole = props.roles.find(
      (role) => role.name === formData.value.role
    );
    if (
      selectedRole &&
      selectedRole.permissions &&
      selectedRole.permissions.includes("all_access") &&
      !isAdmin.value
    ) {
      Swal.fire({
        title: "Error",
        text: "No tienes permisos para asignar roles de administrador",
        icon: "error",
      });
      return;
    }

    isUploading.value = true;

    // Subir avatar a S3 si hay uno pendiente
    if (pendingAvatarFile.value) {
      const uploadResult = await uploadImageToS3(pendingAvatarFile.value);
      if (uploadResult.ok) {
        formData.value.avatar = uploadResult.url;
        pendingAvatarFile.value = null;
      } else {
        Swal.fire({
          title: "Error",
          text: uploadResult.message || "Error al subir el avatar",
          icon: "error",
        });
        isUploading.value = false;
        return;
      }
    }

    // Subir firma a S3 si hay una pendiente
    if (pendingSignatureFile.value) {
      const uploadResult = await uploadImageToS3(pendingSignatureFile.value);
      if (uploadResult.ok) {
        formData.value.signature = uploadResult.url;
        pendingSignatureFile.value = null;
      } else {
        Swal.fire({
          title: "Error",
          text: uploadResult.message || "Error al subir la firma",
          icon: "error",
        });
        isUploading.value = false;
        return;
      }
    }

    let endpoint, requestData;

    if (props.isOwnProfile) {
      // Para perfil propio, usar endpoint de perfil
      endpoint = "/api/users/edit";
      requestData = { user: { ...formData.value } };
    } else {
      // Para edición de admin
      endpoint = isEditing.value ? "/api/users/edit" : "/api/users/register";
      requestData = { user: { ...formData.value } };
    }

    // Si estamos editando y no se quiere cambiar la contraseña, no la enviamos
    if (isEditing.value && !changePassword.value) {
      delete requestData.user.password;
    }

    // Si es perfil propio, no enviar el email ni el rol ya que no deben ser modificados
    if (props.isOwnProfile) {
      delete requestData.user.email;
      delete requestData.user.role;
    }

    const response = await wencesApi.post(endpoint, requestData);

    // Si es perfil propio y se cambió el idioma, actualizar el sistema de traducción
    if (props.isOwnProfile && formData.value.language) {
      syncI18nLocaleFromUser({
        language: formData.value.language,
        business_language: formData.value.business_language,
      });
    }

    if (!props.isOwnProfile) {
      emit("user-updated", response.data.data);
      closeModal();
    } else {
      // Para perfil propio, emitir el evento con los datos actualizados
      emit("user-updated", response.data.data);
    }

    Swal.fire({
      title: isEditing.value ? "¡Actualizado!" : "¡Creado!",
      text: isEditing.value
        ? "El usuario ha sido actualizado correctamente"
        : "El usuario ha sido creado correctamente",
      icon: "success",
    });
  } catch (err) {
    console.error("Error:", err);
    Swal.fire({
      title: "Error",
      text:
        err.response?.data?.error ||
        (isEditing.value
          ? "Hubo un error al actualizar el usuario"
          : "Hubo un error al crear el usuario"),
      icon: "error",
    });
  } finally {
    isUploading.value = false;
  }
};

// Cargar los permisos del usuario actual cuando el componente se monta
onMounted(() => {
  fetchUserPermissions();
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  margin: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.modal-content {
  padding: 0;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.modal-body {
  padding: 1rem;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.modal-footer {
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  background-color: #f8f9fa;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  color: #6c757d;
}

.btn-close:hover {
  color: #343a40;
}

/* Page Mode Styles */
.profile-form-container {
  max-width: 800px;
  margin: 0 auto;
}

.profile-form-container .card {
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.profile-form-container .card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.profile-form-container .form-control:focus {
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}
</style>
